import { useAtomValue, useSetAtom } from 'jotai';

import { useTranslate } from '@i18n';
import { activeData3dAtom, selectedSwingGuideParameterAtom } from '@store';
import { Box, Divider, Heading3, ModalBase, ModalBody, ModalHeader, Stack, SwitchGroup, Text, u } from '@ui';

import { AxisMeaning } from './components/AxisMeaning';
import { SwingGuide3D } from './components/SwingGuide3D';
import { SwingGuideMetrics } from './components/SwingGuideMetrics';
import * as css from './SwingGuide.css';
import { useSwingGuide } from './useSwingGuide';

type SwingGuideProps = {
    parameterId: number;
    onDismiss: () => void;
};

export const SwingGuideLauncher = () => {
    const parameterId = useAtomValue(selectedSwingGuideParameterAtom);
    const setSelectedGuideParameter = useSetAtom(selectedSwingGuideParameterAtom);

    if (!parameterId) {
        return null;
    }
    return <SwingGuide parameterId={parameterId} onDismiss={() => setSelectedGuideParameter(null)} />;
};

export const SwingGuide = ({ parameterId, onDismiss }: SwingGuideProps) => {
    const t = useTranslate();
    const swingGuide = useSwingGuide(parameterId);
    const activeData = useAtomValue(activeData3dAtom);
    if (!swingGuide?.guide) return null;

    const {
        selectedSmpId,
        setSelectedSmpId,
        selectedParameterNode,
        guide,
        selectedGuideAxis,
        selectedGuideParameter,
        availableAxes,
        availableParameters,
        handleAxisChange,
    } = swingGuide;

    const { parameter } = selectedParameterNode;
    const guideTitle = parameter.timeSeriesId
        ? t(`time_series.${parameter.timeSeriesId}`)
        : t(selectedParameterNode.i18n ?? '-');
    const frame =
        activeData?.segmentation[`P${selectedGuideParameter.pPosition}` as keyof typeof activeData.segmentation];

    return (
        <ModalBase onDismiss={onDismiss} open fullHeight variant="partial" fullPartialWidth>
            <ModalHeader>{t('swing_guide.title')}</ModalHeader>
            <ModalBody py={u(4)}>
                <Stack gap={u(4)}>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={u(3)} alignItems="stretch">
                        <Stack flex={1}>
                            <Text variant="h4">{guideTitle}</Text>
                            {guide.guideContent && (
                                <Text variant="p" pb={u(1)}>
                                    {t(guide.guideContent)}
                                </Text>
                            )}
                            {availableAxes.length > 0 && (
                                <SwitchGroup
                                    options={availableAxes}
                                    value={String(selectedGuideAxis.id)}
                                    onClick={handleAxisChange}
                                />
                            )}
                            {selectedGuideAxis.axisContent && (
                                <Text variant="p" pt={u(1)}>
                                    {t(selectedGuideAxis.axisContent)}
                                </Text>
                            )}
                            <AxisMeaning
                                positiveMeaningI18nKey={selectedGuideAxis.positiveMeaning}
                                negativeMeaningI18nKey={selectedGuideAxis.negativeMeaning}
                            />

                            {availableParameters.length > 0 && (
                                <SwitchGroup
                                    options={availableParameters}
                                    value={String(selectedSmpId)}
                                    onClick={(id) => setSelectedSmpId(Number(id))}
                                />
                            )}
                        </Stack>

                        <Box flex={1} className={css.visualizationColumn}>
                            {typeof frame === 'number' && <SwingGuide3D parameter={parameter} frame={frame} />}
                        </Box>
                    </Box>

                    <Divider />

                    <SwingGuideMetrics smpId={selectedSmpId} />

                    {selectedGuideParameter.parameterContent && (
                        <>
                            <Divider />
                            <Stack gap={u(1)}>
                                <Heading3>
                                    {parameter.position?.toUpperCase() ?? 'Swing'} {t('swing_guide.setup')}
                                </Heading3>
                                <Text variant="p">{t(selectedGuideParameter.parameterContent)}</Text>
                            </Stack>
                        </>
                    )}
                </Stack>
            </ModalBody>
        </ModalBase>
    );
};
